import { useEffect, useRef } from "react";
import { isNativePlatform } from "../lib/nativeAuth";

// Android can report one return to the app twice (the page becoming visible
// and Capacitor's "resume"), so a second call this soon after is the same one.
const SAME_RETURN_MS = 2000;

/**
 * Call `onResume` when the collector comes back to the app (B-50).
 *
 * Every screen loaded its data once and then waited on the 30-second poll, so
 * coming back after a short absence showed what was there before it, and the
 * open list itself never reloaded. Two signals cover the two builds: the
 * Capacitor App plugin's "resume" in the Android app, and the page becoming
 * visible again in a browser tab. @capacitor/app is imported lazily and only in
 * the native shell, as backButton.jsx and useAuthDeepLink.js do, so the web
 * bundle and Jest never load it.
 *
 * The latest `onResume` is always the one called; the listeners are attached
 * once per mount.
 */
export default function useAppResume(onResume) {
  const callback = useRef(onResume);
  callback.current = onResume;

  useEffect(() => {
    let last = 0;
    const fire = () => {
      const now = Date.now();
      if (now - last < SAME_RETURN_MS) return;
      last = now;
      callback.current();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") fire();
    };
    document.addEventListener("visibilitychange", onVisible);

    let remove = () => {};
    let cancelled = false;
    if (isNativePlatform()) {
      import("@capacitor/app")
        .then(({ App }) => App.addListener("resume", fire))
        .then((handle) => {
          remove = () => handle.remove();
          if (cancelled) remove();
        })
        .catch(() => {});
    }

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      remove();
    };
  }, []);
}
